/**
 * UI Handler Utilities
 *
 * This file contains utility functions for UI interactions and components
 * extracted from MyCanvas.js to improve code organization and maintainability.
 */

import React from "react";

/**
 * Show snackbar notification
 * @param {string} message - Message to display
 * @param {string} type - Type of notification
 * @param {Function} setSnackbar - Setter for snackbar state
 */
export const createShowSnackbar = (setSnackbar) => (message, type) => {
  setSnackbar({ open: true, message, type });
};

/**
 * Handle replacing a part in selected text
 * @param {Object} selectedPart - Part to replace with
 * @param {Object} selectedText - Currently selected text
 * @param {Function} setSelectedText - Setter for selected text
 * @param {Function} setTexts - Setter for texts array
 * @param {Function} setPartsModalOpen - Setter for parts modal state
 * @param {Function} saveToHistory - Function to save to history
 * @param {Function} generateMarkerId - Function to generate unique IDs
 * @param {Function} getTexts - Function that returns the texts array that contains the selected text (main or off-canvas)
 * @param {Function} getSetTexts - Optional. Returns setter for that array (setTexts or setOffCanvasTexts)
 * @param {Function} getReplacePartSaveArgs - Optional. Returns { currentTextsOverride?, currentOffCanvasTextsOverride?, currentSelectedTextOverride?, currentSelectedTextIdOverride? } for saveToHistory
 * @param {Function} setPendingChanges - Optional. Marks canvas element as dirty for persistence
 * @param {Object} pendingChangesRef - Optional. Synchronous pending changes ref
 */
export const createHandleReplacePart =
  (
    setSelectedText,
    setTexts,
    setPartsModalOpen,
    saveToHistory,
    generateMarkerId,
    onPartReplaced, // Callback to track replaced part references (similar to onPartRemoved)
    getTexts = () => [], // Returns the array that contains the selected text (so we update the right one)
    getSetTexts = null, // () => setTexts | setOffCanvasTexts
    getReplacePartSaveArgs = null, // (updatedTexts, updatedSelectedText, selectedTextId) => overrides
    setPendingChanges = null,
    pendingChangesRef = null,
  ) =>
  (selectedPart, selectedText) => {
    if (!selectedText?.selectedContent?.part) {
      return;
    }

    const oldPart =
      typeof selectedText.selectedContent.part === "object"
        ? selectedText.selectedContent.part.part
        : selectedText.selectedContent.part;
    const oldPartId = selectedText.selectedContent.id;

    let oldTypeName = null;
    let oldPartEntry = null;
    let oldPartData = null; // Capture old part data for tracking

    Object.entries(selectedText.partsByType || {}).forEach(
      ([typeKey, typeData]) => {
        if (typeData.parts?.some((p) => p.id === oldPartId)) {
          oldTypeName = typeKey;
          // Find and capture the old part data before replacement
          const foundPart = typeData.parts.find((p) => p.id === oldPartId);
          if (foundPart) {
            oldPartEntry = foundPart;
            oldPartData = {
              part_ref: foundPart.part_ref || selectedText.selectedContent.part_ref,
              text_box_id: selectedText.text_box_id || selectedText.textboxId || selectedText.textBoxId || selectedText.canvas_text_box_id || selectedText.canvasTextBoxId,
              text_box_content_id: foundPart.text_box_content_id || selectedText.text_box_content_data?.find(
                (entry) => entry.part_ref === (foundPart.part_ref || selectedText.selectedContent.part_ref)
              )?.text_box_content_id,
              units: foundPart.units || foundPart.unit_id || selectedText.selectedContent.units,
              inline_note: foundPart.notePlainText || foundPart.note || "",
            };
          }
        }
      }
    );

    if (!oldTypeName) {
      return;
    }

    const resolvedPartRef =
      selectedPart?.part_id ||
      selectedPart?.part_ref ||
      selectedPart?.id ||
      selectedPart?.partId ||
      "";
    const partNumber =
      selectedPart?.part_number || selectedPart?.partNumber || "";
    const description = selectedPart?.description || "";

    let defaultUnit = "";
    let defaultUnitId = "";
    if (
      selectedPart?.units &&
      selectedPart.units.length > 0 &&
      selectedPart.units[0]?.unit_name
    ) {
      defaultUnit = selectedPart.units[0].unit_name;
      defaultUnitId = selectedPart.units[0].unit_id || "";
    }

    const newPart = {
      id: generateMarkerId(), // ✅ Unique ID for this new instance
      part: `(${partNumber}) ${description}`,
      qty: oldPartEntry?.qty || "",
      unit: oldPartEntry?.unit || "",
      itemCodeNote: selectedPart.textbox_note || "", // System-generated note from Item Code Table (always shows below part)
      note: oldPartEntry?.note || "", // Preserve user note from replaced row
      task: oldPartEntry?.task || "",
      defaultUnit,
      part_ref: resolvedPartRef,
      textbox_note: selectedPart?.textbox_note || "",
      typeName: selectedPart?.type_details?.name || "Other",
      units: defaultUnitId || oldPartEntry?.units || oldPartEntry?.unit_id || "", // Add unit_id
      unit_type_id: selectedPart?.unit_type_id_ref || null, // Add unit type ID for proper unit filtering
    };

    const newType = newPart.typeName;
    const order = parseInt(selectedPart?.type_details?.orders || 9999);

    if (!selectedText.partsByType) {
      setPartsModalOpen(false);
      return;
    }

    const oldTypeParts =
      selectedText.partsByType[oldTypeName]?.parts || [];
    const newTypeParts = selectedText.partsByType[newType]?.parts || [];

    const filteredOldParts = oldTypeParts.filter((p) => p.id !== oldPartId);
    const updatedNewTypeParts =
      oldTypeName === newType
        ? [...filteredOldParts, newPart]
        : [...newTypeParts, newPart];
    const sortedNewTypeParts = updatedNewTypeParts.sort((a, b) =>
      a.part.toLowerCase().localeCompare(b.part.toLowerCase())
    );

    const updatedPartsByType = {
      ...selectedText.partsByType,
      [oldTypeName]: {
        ...selectedText.partsByType[oldTypeName],
        parts: filteredOldParts,
      },
      [newType]: {
        order: selectedText.partsByType[newType]?.order || order,
        parts: sortedNewTypeParts,
      },
    };

    const partContent = `${newPart.part} - Qty-${newPart.qty} ${newPart.unit}`;
    const oldPartRef = oldPartData?.part_ref || selectedText?.selectedContent?.part_ref;
    const existingTextBoxContent = Array.isArray(selectedText?.text_box_content_data)
      ? selectedText.text_box_content_data
      : [];
    const nextTextBoxContentData = existingTextBoxContent
      .map((entry) => {
        if (!entry) return entry;
        if (
          oldPartRef &&
          String(entry.part_ref || "") === String(oldPartRef)
        ) {
          const nextEntry = { ...entry, part_ref: newPart.part_ref };
          if (newPart.units) {
            nextEntry.units = newPart.units;
          }
          return nextEntry;
        }
        return entry;
      })
      .filter(Boolean);

    if (
      newPart.part_ref &&
      !nextTextBoxContentData.some(
        (entry) => String(entry?.part_ref || "") === String(newPart.part_ref),
      )
    ) {
      nextTextBoxContentData.push({
        part_ref: newPart.part_ref,
        qty: parseInt(newPart.qty) || 0,
        inline_note: newPart.note || "",
        ...(newPart.units ? { units: newPart.units } : {}),
      });
    }

    const updatedSelectedText = {
      ...selectedText,
      partsByType: updatedPartsByType,
      text_box_content_data: nextTextBoxContentData,
      selectedContent: {
        id: newPart.id,
        type: "part",
        content: partContent,
        qty: newPart.qty,
        unit: "",
        part: newPart,
        part_ref: newPart.part_ref,
        typeKey: newType,
        unit_type_id: newPart.unit_type_id,
      },
    };

    const currentTexts = typeof getTexts === "function" ? getTexts() : [];
    const updatedTexts = (currentTexts || []).map((text) => {
      if (text?.id === selectedText?.id) {
        return {
          ...text,
          partsByType: updatedPartsByType,
          text_box_content_data: nextTextBoxContentData,
          selectedContent: updatedSelectedText.selectedContent,
        };
      }
      return text;
    });

    setSelectedText(updatedSelectedText);
    const setTextsSetter = typeof getSetTexts === "function" ? getSetTexts() : setTexts;
    setTextsSetter(updatedTexts);
    if (typeof setPendingChanges === "function") {
      setPendingChanges(true);
      if (pendingChangesRef) {
        pendingChangesRef.current = true;
      }
    }
    setPartsModalOpen(false);

    if (onPartReplaced && oldPartData) {
      onPartReplaced(oldPartData);
    }

    // Pass updated state to history so undo captures the replace (closure would still have old state)
    if (typeof getReplacePartSaveArgs === "function") {
      const o = getReplacePartSaveArgs(updatedTexts, updatedSelectedText, selectedText?.id ?? null);
      saveToHistory(
        "replacePart",
        null,
        null,
        null,
        null,
        o.currentTextsOverride ?? null,
        o.currentSelectedTextOverride ?? null,
        o.currentSelectedTextIdOverride ?? null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        o.currentOffCanvasTextsOverride ?? null,
      );
    } else {
      saveToHistory(
        "replacePart",
        null,
        null,
        null,
        null,
        updatedTexts,
        updatedSelectedText,
        selectedText?.id ?? null,
      );
    }
  };

/**
 * Custom Color Picker Component
 * @param {Object} props - Component props
 * @param {string} props.selectedColor - Currently selected color
 * @param {Function} props.setSelectedColor - Setter for selected color
 * @param {Array} props.presetColors - Array of preset colors
 * @param {Object} props.effectiveContentSize - Effective content size
 * @param {number} props.zoomLevel - Current zoom level
 */
export const CustomColorPicker = ({
  selectedColor,
  setSelectedColor,
  presetColors,
  effectiveContentSize,
  zoomLevel,
}) => {
  const applyColorToSelection = (color) => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    const span = document.createElement("span");
    span.style.color = color;
    range.surroundContents(span);
  };

  return (
    <div className="custom-color-picker">
      <div className="color-picker-content">
        <div className="color-picker-left">
          <span
            className="edit-icon"
            style={{ display: "inline-block", borderBottom: "1px solid" }}
          >
            ✎
          </span>

          <div className="color-options">
            {presetColors.map((color) => (
              <div
                key={color}
                className={`color-swatch ${
                  selectedColor === color ? "selected" : ""
                }`}
                style={{ backgroundColor: color }}
                onClick={() => {
                  setSelectedColor(color);
                  applyColorToSelection(color);
                }}
              />
            ))}
          </div>
        </div>
        <div className="hex-input-container">
          <input
            type="text"
            value={selectedColor}
            onChange={(e) => setSelectedColor(e.target.value)}
            className="hex-input"
            placeholder="#000000"
          />
        </div>
        {/* Spacer inside scroll area so vertical scroll can pass overlay tracks */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: `${effectiveContentSize.width}px`,
            height: `${
              effectiveContentSize.height + (zoomLevel > 1 ? 50 : 200)
            }px`,
            pointerEvents: "none",
            opacity: 0,
          }}
        />
      </div>
    </div>
  );
};
